<script lang="ts">
  import moment from "moment";
  import { get } from "lodash";
  import productContainerDetails from "@shared/filters/product_container_details.js";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import PriceWrapper from "@retailer/svelte/Shared/PriceWrapper.svelte";
  import ProductListRowDistributor from "@retailer/svelte/Shared/QuickOrderList/ProductListRowDistributor.svelte";
  import ProductRetailerInventory from "@retailer/svelte/Shared/QuickOrderList/ProductRetailerInventory.svelte";
  import type { Product } from "@shared/types/ProductLine";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { ProductListItemEntity } from "@shared/services/event_tracking/entities";
  import PickupOrDeliveryOutsideCart from "@retailer/svelte/Shared/PickupOrDeliveryOutsideCart.svelte";
  import ListsCartWidgetContainer from "@retailer/svelte/CartWidget/v2/Containers/ListsCartWidgetContainer.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import { hideFulfillmentMethodPickerForDistributor } from "@retailer/services/fulfillment_methods_service";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";

  export let rowProduct: any;
  export let editMode;
  export let callbacks;
  export let readOnly: boolean = false;
  export let showInventory: boolean = false;
  export let disabled: boolean = false;
  export let forSculptureRetailer: boolean = false;
  export let showPrices: boolean = false;
  export let onShowOHLQMessage = null;

  let Cart = window["angularInjector"].get("Cart");
  let cartProduct = Cart.mergeMatchedCartProductToProduct(rowProduct.product);
  let realProduct: Product;
  let hideAllDistributorInfo: boolean = false;

  let productLine;
  if (cartProduct) {
    realProduct = cartProduct.product;
    productLine = cartProduct.product_line;
  } else {
    productLine = rowProduct.product_line_placeholder.product_line;
  }

  let distributor = cartProduct ? cartProduct.distributor : productLine.distributor;

  // auth service
  let authService = getControlStateAuthenticationService();

  $: {
    hideAllDistributorInfo = authService?.shouldHideAllDistributorInfo?.(
      $RetailerUserAuthenticationsStore,
      [distributor?.id],
    );
    if (hideAllDistributorInfo) {
      onShowOHLQMessage?.();
    }
  }

  $: authStatus = authService?.getRetailerUserAuthenticationStatus?.(
    $RetailerUserAuthenticationsStore,
    [distributor?.id],
  );

  // cartProduct needs to be reactive to ensure that inventory changes trickle down into the inventory component
  $: {
    cartProduct = Cart.mergeMatchedCartProductToProduct(rowProduct.product);
  }

  let inventoryChangePending: boolean = false;
  let inventoryUpdateTimestamp: string = "";

  $: {
    const updatedAt = get(rowProduct, "product.retailer_inventory.updated_at");
    inventoryUpdateTimestamp = updatedAt ? moment(updatedAt).format("l") : "";
  }

  const { trackMrcEvents } = EventTracking;
  const entities: ProductListItemEntity[] = [
    new ProductListItemEntity({
      product_id: rowProduct.product_id,
      product_line_id: rowProduct.product?.product_line?.id,
      quick_order_list_id: rowProduct.source_list_id,
      quick_order_list_product_id: rowProduct.id,
    }),
  ];

  let unitName = "bottles";
  $: {
    unitName = cartProduct?.product?.container_type || "bottles";
  }
</script>

<TrackingContext {entities} let:trackingContext>
  <tr
    id="product-{rowProduct.id}"
    data-id={rowProduct.id}
    data-testid="product-{rowProduct.id}"
    class="productRow"
    class:drag-handle={editMode}
    use:trackMrcEvents={[trackingContext.entities]}
  >
    {#if $$slots.selectable && !readOnly && !editMode && !showInventory}
      <td class="selectable fizz-row-select fizz-inset-horizontal-8">
        <p>
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label>
            <slot name="selectable" />
          </label>
        </p>
      </td>
    {:else if editMode}
      <td class="padding--smallest">
        <div class="fizz-inset-square-24" class:cursor-grab={!disabled}>
          <Icon width="24" height="24" type="GrabHandle" />
        </div>
      </td>
    {/if}
    <td>
      <p>
        <UIRouterLink
          stateName="layout.product_detail"
          params={{
            productId: productLine.id,
          }}
          useTrackClick={true}
          targetObjectType="product_list_item_name"
        >
          <strong>{productLine.name}</strong>
        </UIRouterLink>
        <br />
        {#if realProduct && !hideAllDistributorInfo}
          <span class="fizz-text-subdued"
            >{productContainerDetails()(realProduct, null, null, true)}</span
          >
          {#if cartProduct?.inventory?.sku}
            <br />
            <span class="fizz-text-subdued fizz-text-small" data-testid="distributor-sku"
              >SKU: {cartProduct.inventory.sku}</span
            >
          {/if}
        {/if}
      </p>
    </td>

    {#if showInventory && !hideAllDistributorInfo}
      {#if cartProduct}
        <ProductRetailerInventory {cartProduct} bind:inventoryChangePending />
        {#if inventoryChangePending}
          <td>Saving...</td>
        {:else}
          <td>
            {#if inventoryUpdateTimestamp}
              <span class="fizz-hide-on-large">Updated on&nbsp;</span>{inventoryUpdateTimestamp}
            {/if}
          </td>
        {/if}
      {:else}
        <td />
        <td />
      {/if}
    {:else if forSculptureRetailer}
      <td>
        <span class="fizz-hide-on-large fizz-text-strong">On hand:&nbsp;</span>
        {get(rowProduct, "sculpture_report.total_on_hand", "") || ""}
      </td>
      <td>
        <span class="fizz-hide-on-large fizz-text-strong">Par:&nbsp;</span>
        {get(rowProduct, "sculpture_report.total_par", "") || ""}
      </td>
      <td>
        <span class="fizz-hide-on-large fizz-text-strong">Used:&nbsp;</span>
        {get(rowProduct, "sculpture_report.total_consumption", "") || ""}
      </td>
      <td>
        <span class="fizz-hide-on-large fizz-text-strong">Suggested:&nbsp;</span>
        {get(rowProduct, "sculpture_report.order_quantity", "") || ""}
      </td>
    {:else}
      <td>
        <p>
          {productLine.product_type_name}<br />
          {#if productLine.category_name}
            <span class="fizz-text-subdued">{productLine.category_name}</span>
          {/if}
        </p>
      </td>
      <td data-testid="distributor-info">
        {#if !hideAllDistributorInfo}
          <ProductListRowDistributor
            distributor={cartProduct ? cartProduct.distributor : productLine.distributor}
            product={realProduct}
            {productLine}
          />
        {:else}
          -
        {/if}
      </td>
    {/if}
    <td>
      {#if rowProduct.last_ordered_at && !hideAllDistributorInfo}
        <div class="fizz-flex-vertical fizz-no-wrap" data-testid="last-ordered-at">
          <span
            >{($LayoutStore.isMobile ? "Last ordered on " : "") +
              new Date(rowProduct.last_ordered_at).toLocaleDateString()}</span
          >
          <div class="fizz-no-wrap fizz-text-subdued">
            {#if rowProduct.last_ordered_unit_quantity && rowProduct.last_ordered_case_quantity}
              {`${rowProduct.last_ordered_unit_quantity} ${pluralize(
                unitName,
                rowProduct.last_ordered_unit_quantity,
              )}, ${rowProduct.last_ordered_case_quantity}  ${pluralize(
                "case",
                rowProduct.last_ordered_case_quantity,
              )}`}
            {:else if rowProduct.last_ordered_unit_quantity}
              {`${rowProduct.last_ordered_unit_quantity} ${pluralize(
                unitName,
                rowProduct.last_ordered_unit_quantity,
              )}`}
            {:else if rowProduct.last_ordered_case_quantity}
              {`${rowProduct.last_ordered_case_quantity} ${pluralize(
                "case",
                rowProduct.last_ordered_case_quantity,
              )}`}
            {/if}
          </div>
        </div>
      {:else if $LayoutStore.isMobile}
        <div class="fizz-flex-row">
          <span class="fizz-text-regular fizz-text-subdued" style="margin: 0;">
            Last ordered date N/A
          </span>
        </div>
      {:else}
        <div>
          <span class="fizz-text-regular fizz-text-subdued" style="margin: 0;">N/A</span>
        </div>
      {/if}
    </td>

    {#if !editMode}
      {#if cartProduct}
        {#if showPrices}
          <td>
            {#if cartProduct.inventory_id && !hideAllDistributorInfo}
              <PriceWrapper
                inventoryId={cartProduct.inventory_id}
                productTypeId={productLine.product_type_id}
                showPricePerOunce={false}
              >
                <span class="fizz-hide-on-small" data-testid="price-placeholder" slot="placeholder">
                  -
                </span>
              </PriceWrapper>
            {:else}
              <span class="fizz-hide-on-small" data-testid="price-placeholder">-</span>
            {/if}
          </td>
        {/if}
        <td style="vertical-align: middle;" class="fizz-flex-row-responsive">
          {#if !hideAllDistributorInfo}
            <span>
              {#if !stagedRolloutCheck("marketplace__fulfillment_methods") && !hideFulfillmentMethodPickerForDistributor(cartProduct?.distributor?.id)}
                <PickupOrDeliveryOutsideCart
                  product={cartProduct}
                  {Cart}
                  distributor={cartProduct?.distributor}
                  callback={() => {
                    cartProduct = Cart.mergeMatchedCartProductToProduct(rowProduct.product);
                  }}
                  justify="start"
                />
              {/if}
              {#if !cartProduct?.distributor?.name}
                <div>
                  <ProductListRowDistributor
                    distributor={cartProduct.distributor}
                    product={realProduct}
                    {productLine}
                  >
                    <button
                      slot="cta"
                      class="fizz-button fizz-button-primary fizz-full-width"
                      data-testid="list-select-distributor"
                      let:selectDistributor
                      on:click|preventDefault={selectDistributor}
                    >
                      Select a distributor
                    </button>
                  </ProductListRowDistributor>
                </div>
              {:else if cartProduct.inventory_id}
                <ListsCartWidgetContainer
                  inventoryId={cartProduct.inventory_id}
                  distributor={cartProduct.distributor}
                  productId={rowProduct.product.product_id}
                  productLineId={productLine.id}
                  {productLine}
                  on:openDrawer
                />
              {:else}
                <div
                  class="fizz-text-error fizz-flex-row fizz-justify-start fizz-align-self-center fizz-flex-row-0"
                >
                  <Icon type="Info" />
                  <span>
                    This size is unavailable. <a href="/products/{productLine.id}"
                      >See other sizes</a
                    >
                  </span>
                </div>
              {/if}
            </span>
          {:else}
            <div class="fizz-flow-8">
              <span class="fizz-flex-row-4 fizz-align-content-start">
                {#if authStatus == "pending"}
                  <span style="position: relative; top: 2px;">
                    <Icon type="InfoYellow" width="16" height="16" />
                  </span>
                  <p class="fizz-text-left">{authService?.pendingDescription}</p>
                {:else if authStatus == "invalid"}
                  <span style="position: relative; top: 2px;">
                    <Icon type="Error" width="16" height="16" />
                  </span>
                  <p class="fizz-text-left fizz-text-negative">{authService?.invalidDescription}</p>
                {/if}
              </span>
            </div>
          {/if}
        </td>
      {:else}
        {#if showPrices}
          <td>
            <span class="fizz-hide-on-small" data-testid="price-placeholder">-</span>
          </td>
        {/if}
        <td>
          {#if !hideAllDistributorInfo}
            <p>
              <em class="fizz-text-subdued">Size not selected</em><br />
              <a
                href="/"
                on:click|preventDefault={() => {
                  callbacks.onChooseSizeForProductLinePlaceholder(productLine);
                }}
              >
                {productLine.distributor_info && productLine.distributor_info.is_vintage_product
                  ? "Select a vintage and size"
                  : "Select a size"}</a
              >
            </p>
          {:else}
            <div class="fizz-flow-8">
              <span class="fizz-flex-row-4 fizz-align-content-start">
                <span style="position: relative; top: 2px;">
                  <Icon type="InfoYellow" width="16" height="16" />
                </span>
                <p class="fizz-text-left">{authService?.pendingDescription}</p>
              </span>
            </div>
          {/if}
        </td>
      {/if}
    {/if}
  </tr>
</TrackingContext>
